import React from "react";
import "./WellBasedMovement.css";

type Props = {
  style?: React.CSSProperties;
  cols: number;
  rows: number;
  selectedCol: number;
  selectedRow: number;
  onChange: (value: { col: number; row: number }) => void;
};

const getColumnHeaderLetter = (num: number) : string => {
  const numeric = num % 26;
  const letter = String.fromCharCode(65 + numeric);
  const num2 = Math.floor(num / 26);
  if (num2 > 0) {
    return getColumnHeaderLetter(num2 - 1) + letter;
  } else {
    return letter;
  }
};

export default function WellBasedMovement({
  style,
  cols,
  rows,
  selectedCol,
  selectedRow,
  onChange,
}: Props) {
  const maxViewableColumn = 12;
  const maxViewableRow = 8;
  const minCellWidth = 20;
  const minCellHeight = 20;

  const width = (Math.max(cols, maxViewableColumn) + 1) * minCellWidth;
  const height = (Math.max(rows, maxViewableRow) + 1) * minCellHeight;

  const cellWidth = width / (cols + 1);
  const cellHeight = height / (rows + 1);

  const fontSizeWidth = cellWidth / 2;
  const fontSizeHeight = cellHeight / 2;
  const fontSize = (fontSizeWidth + fontSizeHeight) / 2;

  const getCols = (row: number) => {
    let colsView: React.ReactElement[] = [];

    let rowHeaderClassName = "display-table-header ";
    if (row === selectedRow) {
      rowHeaderClassName += "display-table-header-selected ";
    }

    colsView.push(
      <div key={"row-header-" + row} className={rowHeaderClassName}>
        {getColumnHeaderLetter(row)}
      </div>
    );
    let rowClassName = "";

    //first row
    if (row === 0) {
      rowClassName += "display-table-first-row ";
    }

    //last row
    if (row === rows - 1) {
      rowClassName += "display-table-last-row ";
    }

    for (let col = 0; col < cols; col++) {
      let colClassName = "";

      //first col
      if (col === 0) {
        colClassName += "display-table-first-col ";
      }

      //last col
      if (col === cols - 1) {
        colClassName += "display-table-last-col ";
      }

      let style: React.CSSProperties = {
        cursor: "pointer",
        userSelect: "none",
      };

      // let display = col + 1;
      if (col === selectedCol && row === selectedRow) {
        colClassName += "display-table-selected ";
        //display = String.fromCharCode(row + 65) + (col + 1);
        style = {
          ...style,
          fontSize: fontSize * 1.2,
        };
      }

      const key = "cell" + String.fromCharCode(row + 65) + (col + 1);

      colsView.push(
        <div
          key={key}
          id={key}
          style={style}
          className={rowClassName + colClassName}
          onClick={() => onChange({ row, col })}
        >
          <div></div>
        </div>
      );
    }
    return colsView;
  };

  const getColHeader = () => {
    let colsView = [];
    colsView.push(<div key={"table-header"} className="display-table-header"></div>);

    for (let col = 0; col < cols; col++) {
      const className =
        col === selectedCol ? "display-table-header display-table-header-selected" : "display-table-header";
      colsView.push(
        <div key={"col-header-" + col} className={className}>
          {col + 1}
        </div>
      );
    }
    return colsView;
  };

  const getReferenceTable = () => {
    return (
      <div className="well-reference-plate" style={{ width: width, height: height }}>
        <div className="empty"></div>
        <div className="empty0"></div>
        <div className="empty1"></div>
        <div className={selectedCol === 0 ? "well-header header-tl selected" : "well-header header-tl"}>
          <p>1</p>
        </div>
        <div className={selectedCol === cols - 1 ? "well-header header-tr selected" : "well-header header-tr"}>
          <p>{cols}</p>
        </div>
        <div className={selectedRow === 0 ? "well-header header-tl-h selected" : "well-header header-tl-h"}>
          <p>A</p>
        </div>
        <div className={selectedRow === rows - 1 ? "well-header header-bl selected" : "well-header header-bl"}>
          <p>{String.fromCharCode(rows + 65 - 1)}</p>
        </div>
        <div
          className={selectedCol === 0 && selectedRow === 0 ? "well well-tl selected" : "well well-tl"}
          onClick={() => onChange({ col: 0, row: 0 })}
        >
          <p>{selectedCol === 0 && selectedRow === 0 ? "A1" : "1"}</p>
        </div>
        <div
          className={selectedCol === cols - 1 && selectedRow === 0 ? "well well-tr selected" : "well well-tr"}
          onClick={() => onChange({ col: cols - 1, row: 0 })}
        >
          <p>{selectedCol === cols - 1 && selectedRow === 0 ? "A" + cols : cols}</p>
        </div>
        <div
          className={selectedCol === cols - 1 && selectedRow === rows - 1 ? "well well-br selected" : "well well-br"}
          onClick={() => onChange({ col: cols - 1, row: rows - 1 })}
        >
          <p>
            {selectedCol === cols - 1 && selectedRow === rows - 1 ? String.fromCharCode(rows + 65 - 1) + cols : cols}
          </p>
        </div>
        <div
          className={selectedCol === 0 && selectedRow === rows - 1 ? "well well-bl selected" : "well well-bl"}
          onClick={() => onChange({ col: 0, row: rows - 1 })}
        >
          <p>{selectedCol === 0 && selectedRow === rows - 1 ? String.fromCharCode(rows + 65 - 1) + "1" : "1"}</p>
        </div>
      </div>
    );
  };

  let rowsView: React.ReactElement[] = [];

  if (rows * cols === 4) {
    rowsView.push(getReferenceTable());
  } else {
    rowsView.push(<div key={"row-header"} className="display-table-col-header">{getColHeader()}</div>);

    for (let row = 0; row < rows; row++) {
      rowsView.push(<div key={"row-" + row}>{getCols(row)}</div>);
    }
  }

  return (
    <div
      style={{
        width: minCellWidth * 13 + 20,
        height: minCellHeight * 9 + 20,
        overflow: "auto",
        ...style,
      }}
    >
      <div
        style={{
          width: width,
          height: height,
          // width: "100%",
          // height: "100%",
          fontSize: fontSize + "px",
        }}
        className="display-table"
      >
        {rowsView}
      </div>
    </div>
  );
}
